import { GetStaticPaths, GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import React from 'react';

import { ThemeProvider, Themes } from '@/components/context/theme';
import FourOhFour from '@/components/FourOhFour';
import Layout from '@/components/layout';
import CareerHead from '@/components/pages/Career/Head';
import pathsQuery from '@/utils/queries/career/pathsQuery';
import careerQuery from '@/utils/queries/career/singleQuery';
import siteConfigQuery from '@/utils/queries/siteConfigQuery';
import { usePreviewSubscription } from '@/utils/sanity/sanity';
import { getClient, sanityClient } from '@/utils/sanity/sanity.server';
import PlugsResolver from '@/components/plugs/PlugResolver';
import Hero from '@/components/plugs/Hero';
import Button from '@/components/common/Button';

/**
 * [[...slug]] means this is a catch-all route.
 * This means that it needs to handle no slug at all (home)
 * and other pages that have slugs.
 *
 * getStaticPaths: Return an array of pages that will be generated at build time.
 * getStaticProps: Fetch data for given path at build time.
 * usePreviewSubscription: If preview, subscribe to Sanity.
 *
 */

interface Props {
  data: any;
  preview: boolean;
  siteConfig: any;
}

const CareerArticlePage = ({ data, preview, siteConfig }: Props): JSX.Element => {
  const router = useRouter();
  const slug = data?.slug;
  const { data: subscribedData } = usePreviewSubscription(careerQuery, {
    params: { slug },
    initialData: data,
    enabled: preview && slug,
  });

  if (!router.isFallback && !slug) {
    return (
      <ThemeProvider theme={Themes.LIGHT}>
        <Layout siteConfig={siteConfig}>
          <FourOhFour />
        </Layout>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={Themes.LIGHT}>
      <Layout siteConfig={siteConfig} theme="dark">
        <CareerHead data={subscribedData} />
        <Hero title={data.title} text={`${data.type} • ${data.location}`}>
          {data.externalUrl && (
            <Button link={data.externalUrl} theme="primary" title="Apply now" size="large" />
          )}
        </Hero>
        <PlugsResolver plugs={subscribedData?.content} />
      </Layout>
    </ThemeProvider>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const data = await sanityClient.fetch(pathsQuery);

  const paths = data.map((route, key) => {
    const slugEntry = route.slug.toString();
    return {
      params: {
        id: key,
        slug: [slugEntry],
      },
    };
  });

  return {
    paths,
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async ({ params, preview = false }) => {
  const data = await getClient(preview).fetch(careerQuery, { slug: resolveSlug(params.slug) });

  if (!data) {
    return {
      notFound: true,
    };
  }

  const siteConfig = await getClient(preview).fetch(siteConfigQuery);

  if (data?.redirectToExternalPage) {
    return {
      redirect: {
        destination: data.externalUrl,
        permanent: false,
      },
    };
  }

  return {
    revalidate: 60,
    props: {
      data,
      siteConfig,
      preview,
    },
  };
};

const resolveSlug = (slug): string => {
  return `careers/${slug[0]}`;
};

export default CareerArticlePage;
